import type { ReactNode, Ref } from "react";
import { IconNameEnum, useModuleBEM } from "@b2bportal/core-themes";
import { Button, ButtonTypeEnum, Icon } from "@tenant/ui";
import clsx from "clsx";
import styles from "./OfferBanner.module.scss";

/** Text after the "•" separator: "Loaded ✓" (success) or "Applied at checkout" (neutral). */
export interface OfferBannerStatus {
  label: string;
  tone?: "success" | "neutral";
}

/** Trailing primary button, e.g. "View and load" / "Claim now". */
export interface OfferBannerAction {
  label: string;
  onClick: () => void;
  icon?: ReactNode;
  disabled?: boolean;
}

export interface OfferBannerProps {
  /** Leading 36px icon. */
  icon: ReactNode;
  /** Single line, truncated with an ellipsis. */
  title: string;
  status?: OfferBannerStatus;
  /** Drops below the title on mobile. */
  action?: OfferBannerAction;
  /** Makes the whole banner a button; renders the trailing chevron. */
  onClick?: () => void;
  /** Text CTA shown before the chevron; only with `onClick`. */
  ctaLabel?: string;
  /** Accessible name of the banner button; only with `onClick`. */
  ariaLabel?: string;
  /** Shown under the banner. */
  error?: string;
  rootRef?: Ref<HTMLElement>;
  className?: string;
}

/**
 * Offer banner above the hotel list (Figma 39635-71120 / 40548-40219), fed by
 * the 3X earn offer, wallet offers or HTS Media ads. Content is generic: icon,
 * title, status, and either a primary action or a tappable row with chevron.
 */
export const OfferBanner = ({
  icon,
  title,
  status,
  action,
  onClick,
  ctaLabel,
  ariaLabel,
  error,
  rootRef,
  className,
}: OfferBannerProps) => {
  const interactive = onClick != null;
  const [block, cn] = useModuleBEM(styles, "OfferBanner", {
    interactive,
    "with-action": action != null,
  });
  const statusTone = status?.tone ?? "neutral";

  const content = (
    <>
      <div className={cn("main")}>
        <div className={cn("icon")}>{icon}</div>
        <div className={cn("row")}>
          <span className={cn("title")}>{title}</span>
          {status != null && (
            <>
              <span className={cn("separator")}>•</span>
              <span className={cn("status", [statusTone])}>
                {status.label}
                {statusTone === "success" && (
                  <Icon iconName={IconNameEnum.checkFilled} aria-hidden />
                )}
              </span>
            </>
          )}
        </div>
      </div>
      {action != null && (
        <div className={cn("action")}>
          <Button
            buttonType={ButtonTypeEnum.Primary}
            onClick={action.onClick}
            disabled={action.disabled}
            aria-busy={action.disabled}
          >
            {action.icon}
            {action.label}
          </Button>
        </div>
      )}
      {interactive && (
        <div className={cn("trailing")}>
          {ctaLabel != null && <span className={cn("cta")}>{ctaLabel}</span>}
          <Icon
            iconName={IconNameEnum.rightNavChevron}
            className={cn("chevron")}
            useCurrentColor
            aria-hidden
          />
        </div>
      )}
    </>
  );

  const banner = interactive ? (
    <button
      ref={rootRef as Ref<HTMLButtonElement>}
      type="button"
      className={clsx(block, className)}
      onClick={onClick}
      aria-label={ariaLabel}
    >
      {content}
    </button>
  ) : (
    <div
      ref={rootRef as Ref<HTMLDivElement>}
      className={clsx(block, className)}
    >
      {content}
    </div>
  );

  if (error == null) return banner;
  return (
    <>
      {banner}
      <p className={cn("error")} role="alert">
        {error}
      </p>
    </>
  );
};
